import { useState } from "react";
import { Plus, Trash2, ArrowUp, ArrowDown } from "lucide-react";
import { Input, Area, Select, ImageInput, VideoInput, ScheduleControls } from "./fields";
import { FicheOverlay, ItemCard, CardGrid } from "./Fiche";

const uid = () => `vid-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 6)}`;

export const AdminVideos = ({ value = {}, onChange }) => {
  const [open, setOpen] = useState(null);
  const items = value.items || [];
  const cats = value.categories || [];
  const setField = (k, v) => onChange({ ...value, [k]: v });
  const setItems = (arr) => onChange({ ...value, items: arr });
  const patch = (i, p) => { const c = [...items]; c[i] = { ...c[i], ...p }; setItems(c); };
  const move = (i, dir) => { const j = i + dir; if (j < 0 || j >= items.length) return; const c = [...items]; [c[i], c[j]] = [c[j], c[i]]; setItems(c); };
  const add = () => {
    setItems([...items, { id: uid(), title: "Nouvelle vidéo", category: cats[0] || "", videoUrl: "", thumbnail: "", description: "", status: "published", order: items.length }]);
    setOpen(items.length);
  };
  const catOpts = [{ value: "", label: "— Sans catégorie —" }, ...cats.map((c) => ({ value: c, label: c }))];

  return (
    <div className="space-y-6" data-testid="admin-videos-editor">
      <div className="rounded-lg border border-electric/20 bg-[#0a0a0a] p-4 text-sm text-secondary2">
        Importez vos <strong className="text-white">vidéos MP4</strong> directement depuis votre ordinateur (sans YouTube), choisissez une <strong className="text-white">miniature</strong> et une <strong className="text-white">catégorie</strong>. La galerie est accessible sur <a href="/videos" target="_blank" rel="noreferrer" className="text-electric">/videos</a>. Pensez à consulter l'onglet <strong className="text-white">Diagnostic vidéos</strong> pour vérifier leur optimisation.
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <Input label="Sur-titre" value={value.eyebrow} onChange={(v) => setField("eyebrow", v)} />
        <Input label="Titre de section" value={value.heading} onChange={(v) => setField("heading", v)} />
      </div>
      <Area label="Introduction" value={value.intro} onChange={(v) => setField("intro", v)} />
      <Input label="Catégories (séparées par des virgules)" value={cats.join(", ")} onChange={(v) => setField("categories", v.split(",").map((s) => s.trim()).filter(Boolean))} />

      <div className="flex items-center justify-between border-t border-white/10 pt-5">
        <span className="text-[11px] tracking-[0.25em] uppercase text-electric">Vidéos ({items.length})</span>
        <button onClick={add} data-testid="admin-add-video" className="inline-flex items-center gap-2 text-xs text-white/70 hover:text-electric"><Plus size={14} /> Ajouter une vidéo</button>
      </div>

      {open !== null && items[open] ? (
        <FicheOverlay testid="video-fiche" title={items[open].title} subtitle={items[open].category || "Vidéo"} onClose={() => setOpen(null)}>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="md:col-span-2"><Input label="Titre" value={items[open].title} onChange={(v) => patch(open, { title: v })} /></div>
            <Select label="Catégorie" value={items[open].category} onChange={(v) => patch(open, { category: v })} options={catOpts} />
          </div>
          <VideoInput label="Vidéo (MP4 importé ou URL YouTube/Vimeo)" value={items[open].videoUrl} onChange={(v) => patch(open, { videoUrl: v })} />
          <ImageInput label="Miniature (image de couverture)" value={items[open].thumbnail} onChange={(v) => patch(open, { thumbnail: v })} />
          <Area label="Description (optionnel)" value={items[open].description} onChange={(v) => patch(open, { description: v })} rows={2} />
          <ScheduleControls status={items[open].status} publishAt={items[open].publishAt} onChange={(p) => patch(open, p)} />
        </FicheOverlay>
      ) : (
        <>
          {items.length === 0 && <p className="text-secondary2 text-sm">Aucune vidéo. Cliquez sur « Ajouter une vidéo ».</p>}
          <CardGrid testid="videos-cards">
            {items.map((it, i) => (
              <ItemCard key={it.id || i} testid={`admin-video-item-${i}`}
                image={it.thumbnail} title={it.title} subtitle={it.category}
                dimmed={it.status === "hidden"}
                badges={[{ label: it.videoUrl ? "Vidéo liée" : "Sans vidéo", tone: it.videoUrl ? "text-electric border-electric/40 bg-black/60" : "text-amber-300 border-amber-400/40 bg-black/60" }]}
                onOpen={() => setOpen(i)}
                actions={
                  <>
                    <button onClick={() => move(i, -1)} title="Monter" className="p-2 rounded border border-white/10 text-white/40 hover:text-electric"><ArrowUp size={14} /></button>
                    <button onClick={() => move(i, 1)} title="Descendre" className="p-2 rounded border border-white/10 text-white/40 hover:text-electric"><ArrowDown size={14} /></button>
                    <button onClick={() => setItems(items.filter((_, x) => x !== i))} title="Supprimer" className="p-2 rounded border border-white/10 text-red-400/70 hover:text-red-400 hover:border-red-400/40"><Trash2 size={14} /></button>
                  </>
                } />
            ))}
          </CardGrid>
        </>
      )}
    </div>
  );
};
